import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import Icon from '../../Icon';
import { formatMoney } from '../../../utils/format';

function AdminManageWalletView({ user }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('bill'); // 'bill' | 'money'

  const [allUsers, setAllUsers] = useState([]);
  const [balancesById, setBalancesById] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [moneyDescription, setMoneyDescription] = useState('Admin Top Up');

  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  useEffect(() => {
    const loadUsersAndBalances = async () => {
      try {
        setLoadingUsers(true);
        const res = await api.get('/auth/users');
        const users = Array.isArray(res?.data?.users) ? res.data.users : [];
        setAllUsers(users);

        const ids = users.map((u) => u?._id).filter(Boolean);
        if (ids.length === 0) {
          setBalancesById({});
          return;
        }

        const balancesRes = await api.post('/wallet/admin/user-balances', { userIds: ids });
        const items = Array.isArray(balancesRes?.data?.items) ? balancesRes.data.items : [];
        const next = {};
        for (const it of items) {
          if (it?.id) next[String(it.id)] = Number(it.balance || 0);
        }
        setBalancesById(next);
      } catch (e) {
        console.error('Failed to load users/balances', e);
        showNotification('error', e?.response?.data?.error || 'Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsersAndBalances();
  }, []);

  const enrichedUsers = useMemo(() => {
    const list = Array.isArray(allUsers) ? allUsers : [];
    return list
      .map((u) => ({
        ...u,
        _walletBalance: Number(balancesById[String(u?._id)] ?? 0)
      }))
      .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
  }, [allUsers, balancesById]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return enrichedUsers;
    const q = searchQuery.toLowerCase();
    return enrichedUsers.filter((u) => {
      return (
        (u?.name || '').toLowerCase().includes(q) ||
        (u?.email || '').toLowerCase().includes(q) ||
        (u?.phone || '').toLowerCase().includes(q) ||
        (u?.idNumber || '').toLowerCase().includes(q)
      );
    });
  }, [enrichedUsers, searchQuery]);

  const handleSelectUser = (u) => {
    setSelectedUser(u);
    setDropdownOpen(false);
    setSearchQuery('');
  };

  const selectedBalance = selectedUser?._id ? Number(balancesById[String(selectedUser._id)] ?? 0) : 0;
  const numericAmount = Number(amount || 0);
  const projectedBalance = selectedUser
    ? (mode === 'bill' ? (selectedBalance - numericAmount) : (selectedBalance + numericAmount))
    : 0;

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser?._id) {
      showNotification('error', 'Please select a user');
      return;
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      showNotification('error', 'Amount must be greater than 0');
      return;
    }
    const cleanReason = String(reason || '').trim();
    if (mode === 'bill' && !cleanReason) {
      showNotification('error', 'Reason description is required for bills');
      return;
    }
    const cleanMoneyDesc = String(moneyDescription || '').trim() || 'Admin Top Up';

    const name = selectedUser?.name || selectedUser?.email || 'user';
    const confirmMsg =
      mode === 'bill'
        ? `Send bill to ${name}?\n\nCurrent wallet: ${formatMoney(selectedBalance)}\nBill: ${formatMoney(amt)}\nNew wallet: ${formatMoney(selectedBalance - amt)}\n\nReason: ${cleanReason}\n\nBills can make wallets go negative.`
        : `Send money to ${name}?\n\nCurrent wallet: ${formatMoney(selectedBalance)}\nTop up: ${formatMoney(amt)}\nNew wallet: ${formatMoney(selectedBalance + amt)}\n\nDescription: ${cleanMoneyDesc}`;

    if (!window.confirm(confirmMsg)) return;

    try {
      setSubmitting(true);

      let res = null;
      if (mode === 'bill') {
        res = await api.post('/wallet/admin/send-bill', { userId: selectedUser._id, amount: amt, reason: cleanReason });
      } else {
        res = await api.post('/wallet/admin/send-money', { userId: selectedUser._id, amount: amt, description: cleanMoneyDesc });
      }

      showNotification('success', res?.data?.message || (mode === 'bill' ? 'Bill sent' : 'Money sent'));
      const nb = Number(res?.data?.newBalance);
      if (Number.isFinite(nb)) {
        setBalancesById((prev) => ({ ...prev, [String(selectedUser._id)]: nb }));
      } else {
        setBalancesById((prev) => ({ ...prev, [String(selectedUser._id)]: projectedBalance }));
      }

      setAmount('');
      setReason('');
      setMoneyDescription('Admin Top Up');
    } catch (e) {
      console.error('Manage wallet action failed', e);
      showNotification('error', e?.response?.data?.error || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="font-sans">
      {notification && (
        <div className="fixed top-6 right-6 z-50">
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border ${
            notification.type === 'success'
              ? 'bg-emerald-600 border-emerald-500 text-white'
              : 'bg-rose-600 border-rose-500 text-white'
          }`}>
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              {notification.type === 'success' ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <p className="text-sm font-medium">{notification.message}</p>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Manage Wallet</h1>
            <p className="text-sm text-gray-600 mt-1">Send bills (negative) or top up wallets.</p>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 transition-colors"
          >
            <Icon name="arrow-left" size="1rem" />
            Back
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-6 py-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-white">Wallet Actions</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMode('bill')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  mode === 'bill' ? 'bg-white text-emerald-700 border-white' : 'bg-transparent text-white border-white/40 hover:bg-white/10'
                }`}
              >
                Invoice Wallet
              </button>
              <button
                type="button"
                onClick={() => setMode('money')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  mode === 'money' ? 'bg-white text-emerald-700 border-white' : 'bg-transparent text-white border-white/40 hover:bg-white/10'
                }`}
              >
                Fund Wallet
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">User</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((p) => !p)}
                  disabled={loadingUsers}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white text-gray-900 border border-gray-300 hover:border-emerald-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="text-left">
                    {selectedUser
                      ? `${selectedUser.name || 'User'} — ${formatMoney(selectedBalance)}`
                      : loadingUsers
                        ? 'Loading users...'
                        : 'Select a user'}
                  </span>
                  <Icon name={dropdownOpen ? 'chevron-up' : 'chevron-down'} size="1rem" />
                </button>

                {dropdownOpen && (
                  <div className="absolute z-20 mt-2 w-full rounded-lg bg-white text-gray-800 shadow-xl border border-gray-200 overflow-hidden">
                    <div className="p-3 border-b border-gray-200">
                      <input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search name, email, phone, ID"
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {filteredUsers.length === 0 && (
                        <div className="px-4 py-3 text-sm text-gray-500">No users found</div>
                      )}
                      {filteredUsers.map((u) => (
                        <button
                          type="button"
                          key={u._id}
                          onClick={() => handleSelectUser(u)}
                          className="w-full text-left px-4 py-3 hover:bg-emerald-50 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-medium text-gray-800 truncate">{u.name || 'User'}</div>
                              <div className="text-xs text-gray-500 truncate">
                                {u.email || u.phone || u.idNumber || u._id}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                              {formatMoney(Number(u._walletBalance || 0))}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">{mode === 'bill' ? 'Bill Amount' : 'Top Up Amount'}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Amount"
                  className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Projected Wallet Balance</label>
                <div className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 font-semibold">
                  {selectedUser ? formatMoney(projectedBalance) : '—'}
                </div>
              </div>
            </div>

            {mode === 'bill' && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Reason Description</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why are you billing this user?"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            )}

            {mode === 'money' && (
              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">Description</label>
                <input
                  value={moneyDescription}
                  onChange={(e) => setMoneyDescription(e.target.value)}
                  placeholder="e.g. Admin Top Up"
                  className="w-full px-4 py-3 rounded-lg bg-white text-gray-900 placeholder-gray-400 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <p className="text-xs text-gray-500">This text appears in the user’s wallet transactions.</p>
              </div>
            )}

            <div className="flex items-center justify-between gap-4 pt-4 border-t border-gray-200">
              <div className="text-xs text-gray-500">Logged in as {user?.name || 'Admin'}</div>
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-3 rounded-lg bg-emerald-600 text-white font-semibold shadow-md hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : mode === 'bill' ? 'Invoice Wallet' : 'Fund Wallet'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminManageWalletView;
